import { useState } from "react";
import { Select } from "antd";
import { useSelector } from "react-redux";
import SearchBar from "../../components/searchBar";
import CommonTable from "../../components/commonTable";
import ColumnVisibility from "../../components/columnVisibility";
import CustomPagination from "../../components/pagination";
import { getComplimentaryCouponColumns, ComplimentaryCouponRecord } from "./columns";
import { useGetComplimentaryCouponHistoryQuery } from "../../services/complimentaryCoupon";
import usePermissions from "../../hooks/usePermissions";
import "./styles.scss";

/* ─── Module-level constants ─────────────────────────────────────────── */

const SERVICE_TYPE_OPTIONS = [
  { value: 'all',               label: 'All Services'   },
  { value: 'personal_training', label: 'Personal Training' },
  { value: 'pilates',           label: 'Pilates'         },
  { value: 'therapy',           label: 'Therapy'         },
  { value: 'ems',               label: 'EMS'             },
  { value: 'mma',               label: 'MMA'             },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const ComplimentaryCouponHistory = () => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('COMPLIMENTARY_COUPON_VIEW_ALL') || hasPermission('COMPLIMENTARY_COUPON_VIEW_OWN');

  const [searchText,  setSearchText]  = useState("");
  const [serviceType, setServiceType] = useState("all");
  const [page,         setPage]       = useState(1);
  const [limit,        setLimit]      = useState(10);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    sno: true, branch: true, profile: true, name: true, phoneNumber: true,
    serviceType: true, type: true, trainer: true, status: true, startDate: true,
  });

  // branch comes from the global header (Redux), not a per-page filter
  const branchId         = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;

  const { data, isLoading } = useGetComplimentaryCouponHistoryQuery({
    search:      searchText || undefined,
    serviceType: serviceType !== 'all' ? serviceType : undefined,
    branchId:    selectedBranchId || undefined,
    page,
    limit,
  }, { skip: !canView });

  const apiData = data as any;
  const records: (ComplimentaryCouponRecord & { sNo: number })[] = (apiData?.data ?? []).map(
    (r: ComplimentaryCouponRecord, index: number) => ({ ...r, sNo: (page - 1) * limit + index + 1 })
  );
  const total: number = apiData?.total ?? apiData?.pagination?.total ?? records.length;

  const allColumns = getComplimentaryCouponColumns();
  const columns    = allColumns.filter(col => visibleColumns[col.key]);

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  if (!canView) {
    return (
      <div className="complimentary-coupon-history-page">
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)' }}>
          You don't have permission to view coupons history.
        </div>
      </div>
    );
  }

  return (
    <div className="complimentary-coupon-history-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={(val: string) => { setSearchText(val); setPage(1); }}
            placeholder="Search by name or phone..."
          />
          <Select
            value={serviceType}
            onChange={(val: string) => { setServiceType(val); setPage(1); }}
            style={{ width: 180, height: 41 }}
            options={SERVICE_TYPE_OPTIONS}
          />
        </div>
        <div className="right-col">
          <ColumnVisibility
            columns={allColumns}
            visibleColumns={visibleColumns}
            onColumnToggle={handleColumnToggle}
          />
        </div>
      </div>

      <div className="complimentary-coupon-history-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={records}
          loading={isLoading}
          pagination={false}
          rowKey={(record: ComplimentaryCouponRecord, index) => record.sessionId ?? `${record.membershipId}-${record.serviceType}-${index}`}
          scroll={{ x: 1400 }}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={total}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default ComplimentaryCouponHistory;
